import React, { useEffect, useId, useMemo, useRef } from 'react';
import { UploadCloud } from 'lucide-react';

import type {
  SupplierDocument,
  SupplierDocumentType,
  SupplierDocumentValidityRule,
} from '../api';
import { civilDateISO } from '../utils/dates';

const validityRuleLabels: Record<SupplierDocumentValidityRule, string> = {
  with_expiration: 'Com vencimento',
  no_expiration: 'Sem vencimento',
  periodic_update: 'Atualização periódica',
  replace_on_change: 'Substituição por alteração',
};

export function documentTypeByCode(documentTypes: SupplierDocumentType[], code: string) {
  return documentTypes.find((documentType) => documentType.code === code);
}

export function documentTypeName(documentTypes: SupplierDocumentType[], code: string, fallback?: string) {
  return documentTypeByCode(documentTypes, code)?.name || fallback || code.replaceAll('_', ' ');
}

export function documentValidityDescription(documentType?: SupplierDocumentType) {
  if (!documentType) return 'Regra de validade indisponível.';
  if (documentType.validityRule === 'no_expiration') {
    return 'Sem vencimento. O decurso do tempo não gera pendência.';
  }
  if (documentType.validityRule === 'replace_on_change') {
    return 'Substituição por alteração. Permanece vigente até uma nova versão.';
  }
  if (documentType.validityRule === 'periodic_update') {
    const interval = documentType.updateIntervalDays
      ? ` a cada ${documentType.updateIntervalDays} dias`
      : '';
    return `Atualização periódica${interval}. Quando necessário, o status será “Atualização necessária”.`;
  }
  return 'Com vencimento. A data de vencimento é obrigatória.';
}

export function documentVersionDescription(
  document: SupplierDocument,
  documentType: SupplierDocumentType | undefined,
  formatDate: (value?: string | null) => string,
) {
  const validityRule = document.validityRule || documentType?.validityRule;
  if (validityRule === 'no_expiration') return `v${document.version} · Sem vencimento`;
  if (validityRule === 'replace_on_change') return `v${document.version} · Substituição por alteração`;
  if (validityRule === 'periodic_update') {
    const needsUpdate = ['update_required', 'outdated', 'expired'].includes(document.validityStatus || '')
      || document.status === 'vencido'
      || document.status === 'atualizacao_necessaria';
    const issuedAt = formatDate(document.issuedAt);
    return `v${document.version} · emissão/consulta em ${issuedAt} · ${needsUpdate ? 'Atualização necessária' : 'Atualização periódica'}`;
  }
  return `v${document.version} · vence em ${formatDate(document.expiresAt)}`;
}

export function documentDisplayStatus(
  document: SupplierDocument,
  documentType?: SupplierDocumentType,
) {
  const validityRule = document.validityRule || documentType?.validityRule;
  if (validityRule === 'periodic_update' && (
    ['update_required', 'outdated', 'expired'].includes(document.validityStatus || '')
    || document.status === 'vencido'
    || document.status === 'atualizacao_necessaria'
  )) {
    return 'atualização necessária';
  }
  const labels: Record<string, string> = {
    enviado: 'enviado',
    aprovado: 'aprovado',
    reprovado: 'reprovado',
    vencido: 'vencido',
    a_vencer: 'a vencer',
  };
  return labels[document.status] || document.status;
}

interface SupplierDocumentUploadProps {
  title?: string;
  documentTypes: SupplierDocumentType[];
  documentType: string;
  onDocumentTypeChange: (value: string) => void;
  expiresAt: string;
  onExpiresAtChange: (value: string) => void;
  issuedAt: string;
  onIssuedAtChange: (value: string) => void;
  file: File | null;
  onFileChange: (file: File | null) => void;
  onSubmit: () => void;
  submitting: boolean;
  submitLabel: string;
  className?: string;
}

export const SupplierDocumentUpload: React.FC<SupplierDocumentUploadProps> = ({
  title = 'Upload de documento',
  documentTypes,
  documentType,
  onDocumentTypeChange,
  expiresAt,
  onExpiresAtChange,
  issuedAt,
  onIssuedAtChange,
  file,
  onFileChange,
  onSubmit,
  submitting,
  submitLabel,
  className = '',
}) => {
  const inputId = useId();
  const typeId = `${inputId}-type`;
  const expiresId = `${inputId}-expires`;
  const issuedId = `${inputId}-issued`;
  const issuedErrorId = `${issuedId}-error`;
  const fileId = `${inputId}-file`;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const selectedType = useMemo(
    () => documentTypeByCode(documentTypes, documentType),
    [documentType, documentTypes],
  );
  const requiresExpiration = selectedType?.validityRule === 'with_expiration';
  const requiresIssuedAt = selectedType?.validityRule === 'periodic_update';
  const currentCivilDate = civilDateISO();
  const issuedAtIsFuture = requiresIssuedAt && Boolean(issuedAt) && issuedAt > currentCivilDate;

  useEffect(() => {
    if (!selectedType && documentTypes[0]) onDocumentTypeChange(documentTypes[0].code);
  }, [documentTypes, onDocumentTypeChange, selectedType]);

  useEffect(() => {
    if (selectedType && selectedType.validityRule !== 'with_expiration' && expiresAt) {
      onExpiresAtChange('');
    }
  }, [expiresAt, onExpiresAtChange, selectedType]);

  useEffect(() => {
    if (selectedType && selectedType.validityRule !== 'periodic_update' && issuedAt) {
      onIssuedAtChange('');
    }
  }, [issuedAt, onIssuedAtChange, selectedType]);

  useEffect(() => {
    if (!file && fileInputRef.current) fileInputRef.current.value = '';
  }, [file]);

  const canSubmit = Boolean(
    file
    && selectedType
    && (!requiresExpiration || expiresAt)
    && (!requiresIssuedAt || (issuedAt && !issuedAtIsFuture)),
  );

  return (
    <div className={`rounded-lg border border-dashed border-gray-300 p-4 ${className}`}>
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900">
        <UploadCloud className="h-4 w-4 text-blue-600" />
        {title}
      </div>
      <div className="grid gap-3">
        <label htmlFor={typeId} className="text-sm font-medium text-gray-700">
          Tipo de documento
        </label>
        <select
          id={typeId}
          value={selectedType?.code || ''}
          onChange={(event) => onDocumentTypeChange(event.target.value)}
          disabled={documentTypes.length === 0}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100"
        >
          {documentTypes.length === 0 && <option value="">Catálogo documental indisponível</option>}
          {documentTypes.map((item) => (
            <option key={item.code} value={item.code}>
              {item.name} · {validityRuleLabels[item.validityRule]}
            </option>
          ))}
        </select>

        {selectedType && (
          <p className="text-xs text-gray-600" role="status">
            {documentValidityDescription(selectedType)}
          </p>
        )}

        {requiresExpiration && (
          <label htmlFor={expiresId} className="text-sm font-medium text-gray-700">
            Data de vencimento <span className="text-red-500">*</span>
            <input
              id={expiresId}
              type="date"
              required
              value={expiresAt}
              onChange={(event) => onExpiresAtChange(event.target.value)}
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </label>
        )}

        {requiresIssuedAt && (
          <label htmlFor={issuedId} className="text-sm font-medium text-gray-700">
            Data de emissão/consulta <span className="text-red-500">*</span>
            <input
              id={issuedId}
              type="date"
              required
              max={currentCivilDate}
              value={issuedAt}
              onChange={(event) => onIssuedAtChange(event.target.value)}
              aria-invalid={issuedAtIsFuture}
              aria-describedby={issuedAtIsFuture ? issuedErrorId : undefined}
              className={`mt-1 block w-full rounded-lg border px-3 py-2 text-sm ${issuedAtIsFuture ? 'border-red-400' : 'border-gray-300'}`}
            />
            {issuedAtIsFuture && (
              <span id={issuedErrorId} role="alert" className="mt-1 block text-xs text-red-600">
                A data de emissão/consulta não pode ser futura.
              </span>
            )}
          </label>
        )}

        <label htmlFor={fileId} className="text-sm font-medium text-gray-700">
          Arquivo <span className="text-red-500">*</span>
        </label>
        <input
          ref={fileInputRef}
          id={fileId}
          type="file"
          required
          onChange={(event) => onFileChange(event.target.files?.[0] || null)}
          className="text-sm"
        />
        <button
          type="button"
          onClick={onSubmit}
          disabled={!canSubmit || submitting}
          aria-disabled={!canSubmit || submitting}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? 'Enviando...' : submitLabel}
        </button>
      </div>
    </div>
  );
};
